import { Flame } from "lucide-react";

interface MacroDonutProps {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  sugar: number | null | undefined;
  fiber: number;
  sodium: number;
  t: (k: string) => string;
  dir: "rtl" | "ltr";
}

export function MacroDonut({ calories, protein, carbs, fat, sugar, fiber, sodium, t, dir }: MacroDonutProps) {
  const pCal = protein * 4, cCal = carbs * 4, fCal = fat * 9;
  const tot = pCal + cCal + fCal || 1;
  const pPct = Math.round((pCal / tot) * 100);
  const fPct = Math.round((fCal / tot) * 100);
  const cPct = 100 - pPct - fPct;

  const W = 32, DX = 10, DY = -7, MAX_H = 110, BASE_Y = 155;
  const xs = [10, 54, 98, 142, 186, 230];

  const sugarKnown = typeof sugar === "number";
  const sugarVal = sugarKnown ? sugar as number : 0;
  const sugarPct = sugarKnown ? Math.min(100, Math.round(sugarVal / 50 * 100)) : null;
  const sugarDisplay = sugarKnown ? `${sugarVal}g` : (dir === "rtl" ? "غير متوفر" : "N/A");

  const bars = [
    { label: t("protein"), val: protein, pct: pPct, unit: "g",
      front: "#22d3ee", top: "#cffafe", side: "#0891b2" },
    { label: t("fat"),     val: fat,     pct: fPct, unit: "g",
      front: "#f87171", top: "#fee2e2", side: "#b91c1c" },
    { label: t("carbs"),   val: carbs,   pct: cPct, unit: "g",
      front: "#4ade80", top: "#dcfce7", side: "#15803d" },
    { label: t("sugar"),   val: sugarVal, pct: sugarPct, unit: "g", display: sugarDisplay, known: sugarKnown,
      front: "#f472b6", top: "#fce7f3", side: "#9d174d" },
    { label: t("fiber"),   val: fiber,   pct: Math.min(100, Math.round(fiber  / 30   * 100)), unit: "g",
      front: "#34d399", top: "#d1fae5", side: "#065f46" },
    { label: t("sodium"),  val: sodium,  pct: Math.min(100, Math.round(sodium / 2300 * 100)), unit: "mg",
      front: "#60a5fa", top: "#dbeafe", side: "#1e40af" },
  ];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-4 h-4 text-green-600" />
        <h3 className="font-bold text-stone-700 dark:text-slate-200 text-sm">{dir === "rtl" ? "معلومات التغذية" : "Nutrition Info"}</h3>
      </div>
      <div className="flex justify-center mb-4">
        <div className="inline-flex items-baseline gap-1.5 px-6 py-2.5 bg-yellow-50 dark:bg-yellow-500/10 rounded-2xl border border-yellow-200 dark:border-yellow-500/20">
          <span className="text-4xl font-black text-yellow-700 dark:text-yellow-400 leading-none">{calories}</span>
          <span className="text-sm font-bold text-yellow-700 dark:text-yellow-500">kcal</span>
        </div>
      </div>
      <svg viewBox="0 0 282 200" className="w-full">
        <ellipse cx="136" cy={BASE_Y + 10} rx="130" ry="7" fill="currentColor" className="text-stone-100 dark:text-slate-700/60" />
        {bars.map((bar, i) => {
          const barPct = bar.pct as number | null;
          const isUnknown = barPct === null;
          const barH = isUnknown ? 5 : Math.max(5, (barPct / 100) * MAX_H);
          const x = xs[i];
          const y = BASE_Y;
          const fp = `${x},${y} ${x+W},${y} ${x+W},${y-barH} ${x},${y-barH}`;
          const tp = `${x},${y-barH} ${x+W},${y-barH} ${x+W+DX},${y-barH+DY} ${x+DX},${y-barH+DY}`;
          const sp = `${x+W},${y} ${x+W+DX},${y+DY} ${x+W+DX},${y-barH+DY} ${x+W},${y-barH}`;
          const hp = `${x+4},${y-barH+4} ${x+12},${y-barH+4} ${x+12},${y-6} ${x+4},${y-6}`;
          const valText = (bar as { display?: string }).display ?? `${bar.val}${bar.unit}`;
          const dimClass = (bar as { known?: boolean }).known === false ? "opacity-40" : "";
          const pctText = isUnknown ? "—" : `${barPct}%`;
          return (
            <g key={i} className={dimClass}>
              {!isUnknown && <polygon points={sp} fill={bar.side} />}
              {!isUnknown && <polygon points={fp} fill={bar.front} />}
              {!isUnknown && <polygon points={hp} fill="white" opacity={0.22} />}
              {!isUnknown && <polygon points={tp} fill={bar.top} />}
              {!isUnknown && <polygon points={`${x},${y} ${x+W},${y} ${x+W},${y+15} ${x},${y+15}`}
                fill={bar.front} opacity={0.12}
                transform={`scale(1,-1) translate(0,${-(2*y+15)})`} />}
              <text x={x+W/2} y={y-barH-7} textAnchor="middle" fontSize="10" fontWeight="800"
                className="fill-yellow-500 dark:fill-yellow-400">{pctText}</text>
              <text x={x+W/2} y={y+18} textAnchor="middle" fontSize="8.5" fontWeight="600"
                className="fill-yellow-600 dark:fill-yellow-400">{bar.label}</text>
              <text x={x+W/2} y={y+29} textAnchor="middle" fontSize="8"
                className="fill-yellow-500 dark:fill-yellow-500">{valText}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
